import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { DEFAULT_PACKAGES } from "@/lib/packages";
import { DEVICES } from "@/lib/devices";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { getMeta } from "@/seo/pages";
import { HOME_JSONLD, DEFINITION, BUYER_FAQ_HOME } from "@/seo/homeJsonld";
import { trackPage, getAttribution } from "@/lib/track";
import { AccountMenu } from "@/components/account";
import "@/styles/home.css";

const QUESTIONS = [
  ["Which of the generated test cases actually ran on a real device, and which were never executed?", "Coverage", "A test case that was generated but never run is a hypothesis. The report shows every case against every device with pass, fail, blocked or not run — and says why for the last two."],
  ["Does checkout survive an app switch to a UPI app on a budget Android with aggressive battery management?", "Real devices", "MIUI and ColorOS kill backgrounded apps by default. This is the single most common S1 our testers find, and it does not reproduce on an emulator."],
  ["For each failure, can a developer reproduce it from the report alone — without asking who found it?", "Reproducibility", "Every accepted finding has steps, expected, actual, device, OS version, network and a screen recording. Findings without them are rejected before they reach you."],
  ["How many of the bugs in your tracker are the same bug reported three times?", "Verification", "Duplicates are merged during verification, and the report tells you how many were merged so the count you're looking at is the count you have."],
  ["Who decided that bug was an S2 and not an S1, and against what rule?", "Severity", "Severity is graded against a published rubric with a confidence score. When the verifier changes a tester's grade, the report says so and why."],
  ["What happens on Safari on a MacBook, or Chrome on the five-year-old Windows laptop your customer's office still runs?", "Browser matrix", "You set targets by browser and OS as well as by phone. Sessions are matched to testers who own those exact machines, the session page records the real browser version and viewport, and the matrix in the report proves it."],
  ["When the fix ships, will the same cases be re-run on the same devices?", "Re-run", "Every report has a one-click re-run on the same plan with a new build. Same devices, same cases, new results — so you can show the fix rather than assert it."],
];

const DELIV = [
  ["Coverage table", "Every test case against every device — pass, fail, blocked or not run. Fails link straight to the finding."],
  ["Reproducible findings", "Steps, expected, actual, device, OS, network and a screen recording on every serious bug."],
  ["Verification record", "How many findings were accepted, merged or rejected, the rubric version, and a confidence score on each."],
  ["Device and browser matrix", "Which phone or laptop, which OS build, which browser version and viewport, which network, which session — so \"tested on Android\" or \"works on Chrome\" means something."],
  ["Session log", "Start and end times, tester band, cases assigned and cases completed for every session that ran."],
  ["Coverage note", "Anything that didn't run, and why. Plus a one-click re-run on the same plan when your fix is ready."],
];

const HOME_STATS = [
  ["300+", "companies tested with 99tests"],
  ["127,000+", "bugs reported and verified"],
  ["20,000", "testers registered across India"],
  ["Since 2010", "running crowdsourced testing"],
];
const HOME_TESTIMONIALS = [
  { quote: "The use of crowd concept emulating the real world delivered a unique insight of our customer's experience.", name: "Steve Coles", title: "CTO, Allianz Insurance", note: "Also an investor in 99tests" },
  { quote: "As a company, we have made great strides in improving our website quality thanks to continued crowdsourced testing with 99tests.", name: "Daniel Yates", title: "Founder, Pitchup" },
  { quote: "99tests has been an invaluable QA partner for us over the last several months, right from Day 1.", name: "Mekin Maheshwari", title: "Chief People Officer, Flipkart" },
];
const BARS = [
  ["Teams that use AI in testing", 94, false, "BrowserStack, 2024 — State of Testing & Quality", "https://www.browserstack.com"],
  ["Teams that have reached full autonomy", 12, true, "BrowserStack, 2024 — State of Testing & Quality", "https://www.browserstack.com"],
  ["India's smartphone users who are on Android", 91, false, "Counterpoint Research, 2024", null],
  ["US web traffic on Safari — the browser most teams skip", 29, true, "StatCounter, 2026", "https://gs.statcounter.com"],
];

const FAQ = [
  ["What formats do you accept for test cases?", "CSV, Excel, Google Sheets export, Word, Markdown, plain text, and TestRail or Jira exports. AI-generated cases from any tool are fine. We parse them into a plan and show you the coverage before testers start."],
  ["How do I share the build?", "An APK download link, a Google Play internal testing link, a TestFlight public link, or for web apps a staging or production URL. If it needs test credentials or basic auth, include them in the submission form; they are shown only to assigned testers."],
  ["Which browsers and laptops do you cover?", "Chrome, Safari, Edge and Firefox on Windows, macOS and ChromeOS, on testers' own laptops and desktops. You choose the browser and OS as targets the same way you choose a phone. The session records the actual browser version and screen size, and the report shows them."],
  ["Who chooses the devices?", "You set targets — brand and minimum OS for phones, browser and OS for laptops. We match testers who own those devices. If a match can't be found within your turnaround we widen once, and if it still can't be filled the unfilled sessions are credited back to your email and applied to your next job."],
  ["Is my app confidential?", "Testers accept confidentiality terms before they can take sessions. Builds are shared by link only to assigned testers and are not stored by 99tests beyond the job. Recordings are stored privately and linked only from your report."],
  ["Do you invoice with GST?", "Yes, for Indian customers. Enter your GSTIN at checkout and Stripe issues a compliant invoice immediately. Customers outside India are billed in US dollars and receive a standard invoice."],
  ["What if there are more test cases than the package covers?", "After upload you'll see exactly which cases are uncovered and can add five sessions at a time. Or leave them; the report lists them as not run."],
  ["Can I get a custom scope?", "Not yet. The three packages cover most release checks, and blocks of five sessions cover the rest."],
  ["What if the report is late?", "If we miss the report deadline shown on your invoice, get in touch and we'll make it right. The only exception is when your build or test credentials weren't available to our testers."],
];

const TIMELINE = [
  ["Minute 0", "Pay", "Choose a package, pay by card. Invoice issued at checkout."],
  ["Minute 2", "Submit", "Build link or web URL, test cases in any format, device and browser targets, deadline."],
  ["Minute 5", "Plan", "Cases parsed into sessions by device. You see exactly what will run."],
  ["Hour 1", "Dispatch", "Offers go to testers who own your target phones and laptops. First to accept takes the session."],
  ["Day 1–2", "Sessions", "Timed 45- or 90-minute sessions. Findings recorded live with media."],
  ["Day 3", "Verify and deliver", "Findings checked, merged, graded. Report emailed as page and PDF."],
];

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/tools" data-testid="nav-tools">Free tools</a>
        <a href="/pricing">Pricing</a>
        <a href="/research" data-testid="nav-research">Research</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <AccountMenu />
        <a className="btn sm blue" href="/pricing" data-testid="start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function LiveCard() {
  const [acc, setAcc] = useState(6);
  const [fnd, setFnd] = useState(3);
  const [r3, setR3] = useState(false);
  const [mins, setMins] = useState(23);
  useEffect(() => {
    const t = setInterval(() => setMins((m) => m + 1), 60000);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return () => clearInterval(t);
    const a = setTimeout(() => { setR3(true); setAcc(7); }, 2600);
    const b = setTimeout(() => setFnd(4), 5200);
    return () => { clearTimeout(a); clearTimeout(b); clearInterval(t); };
  }, []);
  const dueLabel = (() => {
    const d = new Date(); let n = 0;
    while (n < 3) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) n++; }
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  })();
  const now = new Date();
  const doy = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const jobNum = `99T-${2000 + doy}`;
  return (
    <div className="live" data-testid="live-job-card" aria-label="Illustration of a live job in progress — sample data">
      <div className="cap"><b>Job {jobNum} · Standard</b><span className="lv"><i></i>Running</span></div>
      <div className="grid">
        <div className="cellx"><div className="lbl">Sessions accepted</div><div className="big">{acc}<small>of 10</small></div></div>
        <div className="cellx"><div className="lbl">Findings so far</div><div className="big">{fnd}<small>2 verified</small></div></div>
        <div className="cellx"><div className="lbl">Cases covered</div><div className="big">47<small>of 58 parsed</small></div></div>
        <div className="cellx"><div className="lbl">Report due</div><div className="big" style={{ fontSize: 22 }}>{dueLabel}</div></div>
      </div>
      <div className="rows">
        <div className="r"><span className="d">Redmi 12<small>Android 13 · MIUI 14 · Jio 4G</small></span><span className="pill">In session</span></div>
        <div className="r"><span className="d">Galaxy A15<small>Android 14 · One UI 6 · Vi 4G</small></span><span className="pill">Submitted</span></div>
        <div className="r"><span className="d">Realme C55<small>Android 13 · realme UI 4</small></span><span className={r3 ? "pill" : "pill neutral"}>{r3 ? "Accepted" : "Offered"}</span></div>
        <div className="r"><span className="d">iPhone 11<small>iOS 17.6 · Airtel 4G</small></span><span className="pill">In session</span></div>
        <div className="r"><span className="d">Chrome 128 · Windows 11<small>Laptop · 1366×768 · Airtel broadband</small></span><span className="pill">Submitted</span></div>
      </div>
      <div className="foot"><span>Verification <b>2 accepted · 1 pending</b></span><span>Started <b>{mins} min ago</b></span></div>
    </div>
  );
}

function Bars() {
  const ref = useRef(null);
  const [fill, setFill] = useState(false);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) { setFill(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setFill(true); io.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div className="bars" ref={ref} data-testid="gap-bars">
      {BARS.map(([label, w, low, src, url]) => (
        <div className="barrow" key={label}>
          <div className="top"><span>{label}</span><b>{w}%</b></div>
          <div className="bar"><i className={low ? "low" : ""} style={{ width: fill ? `${w}%` : 0 }}></i></div>
          <small>Source: {url ? <a href={url} target="_blank" rel="noopener noreferrer" data-testid="stat-source">{src}</a> : <span data-testid="stat-source">{src}</span>}</small>
        </div>
      ))}
      <p className="muted small" style={{ margin: 0 }}>Figures from the linked sources, latest published at the time of writing. Your users are on these devices, browsers and networks — not on your emulator.</p>
    </div>
  );
}

export default function Home() {
  useSeo({ path: "/", description: getMeta("/").description, jsonld: HOME_JSONLD });
  useEffect(() => { trackPage("/"); }, []);
  const { data } = useQuery({ queryKey: ["config"], queryFn: api.config });
  const [cur, setCur] = useState("INR");
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap")) return;
    try {
      const saved = localStorage.getItem("99t_cur");
      if (saved === "INR" || saved === "USD") { setCur(saved); return; }
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (!(tz === "Asia/Kolkata" || tz === "Asia/Calcutta")) setCur("USD");
    } catch (e) {}
  }, []);

  function pickCur(c) { setCur(c); try { localStorage.setItem("99t_cur", c); } catch (e) {} }

  const [pending, setPending] = useState(null);
  const [email, setEmail] = useState(() => { try { return localStorage.getItem("99t_email") || ""; } catch (e) { return ""; } });
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    if (!pending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCredit(null); return; }
    const t = setTimeout(() => api.creditBalance(email, cur).then((r) => setCredit(r.balance_minor)).catch(() => setCredit(null)), 300);
    return () => clearTimeout(t);
  }, [email, pending, cur]);

  function buy(pkg) { setPending(pkg); }

  async function confirmBuy() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter the email for your invoice and report"); return; }
    setLoading(pending);
    try {
      try { localStorage.setItem("99t_email", email); } catch (e) {}
      const attr = getAttribution() || {};
      const res = await api.checkout({ package: pending, cur: cur.toLowerCase(), email,
        country: cur === "INR" ? "IN" : "US", origin_url: window.location.origin,
        landing: attr.landing || "/", source: attr.source || null,
        medium: attr.medium || null, campaign: attr.campaign || null });
      if (res.paid_by_credit) toast.success("Covered by your credit — no payment needed");
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  }

  const packages = data?.packages || DEFAULT_PACKAGES;
  const order = ["starter", "standard", "deep"];
  const kickers = { starter: "One flow, two phones.", standard: "A release candidate across your main devices.", deep: "Longer exploratory sessions, wider matrix." };
  const cases = { starter: "up to ~25", standard: "up to ~50", deep: "up to ~120" };
  const devNote = { starter: "your choice", standard: "mix freely", deep: "mix freely" };

  return (
    <>
      <Header />
      <main>
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Fixed price</b><span className="dot"></span><b>3 business days</b><span className="dot"></span><span>Phones · laptops · browsers</span></div>
              <h1>Your AI wrote the test cases.<br />Now run them on <em>real phones and browsers.</em></h1>
              <p className="lede" data-testid="home-definition">{DEFINITION}</p>
              <p className="lede">Run your AI-generated test cases on real phones, laptops and browsers your users own. Built for teams shipping with AI coding tools.</p>
              <p className="fact">From {money(packages.starter?.prices?.[cur]?.display_minor || 0, cur)}. No account, no sales call. Testers are on it within the hour.</p>
              <p className="muted small" data-testid="home-trust-stats" style={{ marginTop: 10 }}>300+ companies · 127,000+ bugs reported · 20,000 registered testers · since 2010</p>
              <p className="muted small" data-testid="home-founder-line" style={{ marginTop: 4 }}>Run by Praveen Singh, who founded 99tests in 2010 — <a href="/about">About us</a>.</p>
              <div className="actions">
                <a className="btn blue" href="/pricing">See packages</a>
                <a className="btn ghost" href="/sample-report/" data-testid="sample-report-hero-link">Read a sample report</a>
              </div>
            </div>
            <LiveCard />
          </div>
        </section>

        <section id="how-ai" className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">How it works</span><h2>From AI-generated cases to a human-verified report.</h2></div>
            <div className="tl" data-testid="how-ai-steps">
              <div><b>Step 1</b><h4>Upload</h4><p>Upload your AI-generated test cases.</p></div>
              <div><b>Step 2</b><h4>Pick devices</h4><p>Pick the devices and browsers your users actually own — Redmi, Samsung, iPhone, Chrome, Safari and more.</p></div>
              <div><b>Step 3</b><h4>Real testers run them</h4><p>Real testers run every case by hand on real hardware.</p></div>
            </div>
            <div className="section-head" style={{ marginTop: 48 }}><span className="eyebrow">What you get in 3 business days</span><h2>A report a developer can act on.</h2></div>
            <div className="proof" data-testid="what-you-get">
              <div><h4>Pass or fail, per case</h4><p>A human-verified result for every test case you upload — not a summary, but each case marked pass or fail.</p></div>
              <div><h4>A screenshot of every failure</h4><p>Each failing case comes with a screenshot showing exactly what the tester saw on the real device.</p></div>
              <div><h4>Exact reproduction steps</h4><p>Step-by-step repro for every failure, so a developer can reproduce it without asking who found it.</p></div>
              <div><h4>Device, OS and browser details</h4><p>The real phone, OS build and browser version each case ran on — the context an emulator can't give you.</p></div>
            </div>
            <p className="lede" style={{ marginTop: 24 }}>Catch the failures that emulators, simulators and AI agent test runs miss — before your users find them.</p>
          </div>
        </section>

        <section style={{ paddingTop: 0, borderTop: 0 }}>
          <div className="wrap">
            <div className="proof">
              <div><h4>The AI test case is not the test</h4><p>Generated cases describe intent. Only a person on a device can tell you whether the app does it.</p></div>
              <div><h4>Emulators are not real devices</h4><p>MIUI kills background tasks. Jio drops mid-payment. Safari blocks the popup. A 2019 Windows laptop on Chrome scrolls differently. Device clouds don't show you that.</p></div>
              <div><h4>Every finding is verified</h4><p>Incomplete reports are rejected, duplicates merged, severity graded against a published rubric before you see them.</p></div>
              <div><h4>Unfilled sessions are credited</h4><p>If we can't match a device in your turnaround, the sessions that didn't run come back as credit on your email, applied to your next job.</p></div>
            </div>
          </div>
        </section>

        <section className="band" id="gap">
          <div className="wrap">
            <div className="gap">
              <div>
                <span className="eyebrow">The gap</span>
                <h2>Teams generate tests faster than they can run them.</h2>
                <p className="lede">AI test generation has been adopted across the industry. Real-device execution has not caught up.</p>
                <p>The cases exist. What doesn't exist is the afternoon someone spends on a ₹9,000 Android with a flaky connection, following them one by one and writing down what actually happened. Most releases ship without that afternoon. The ones that had it were surprised by what it found.</p>
                <p>Automation answers "does the script still pass". A person on a phone — or on Safari on a MacBook, or Chrome on an office Windows laptop — answers "does this work for the customer in Hubballi". Those are different questions, and only one of them is in your release checklist.</p>
              </div>
              <Bars />
            </div>
          </div>
        </section>

        <section id="questions">
          <div className="wrap">
            <div className="section-head">
              <span className="eyebrow">Before you ship</span>
              <h2>Seven questions your release manager gets asked.</h2>
              <p>If any of these takes more than a minute to answer, the gap is in your evidence, not your test cases.</p>
            </div>
            <div className="qs" data-testid="seven-questions">
              {QUESTIONS.map(([t, tg, p], i) => (
                <details className="q" key={i} open={i === 0}>
                  <summary><span className="n">{String(i + 1).padStart(2, "0")}</span><span className="t">{t}</span><span className="tg">{tg}</span></summary>
                  <p>{p}</p>
                </details>
              ))}
            </div>
            <p className="muted small" style={{ marginTop: 18 }}>We leave the "evidence" column of your release checklist to the report, not to a meeting.</p>
          </div>
        </section>

        <section className="band" id="report">
          <div className="wrap">
            <div className="section-head">
              <span className="eyebrow">What you receive</span>
              <h2>Six things in every report. One fixed price.</h2>
              <p>Delivered as a hosted page and a PDF, addressed to whoever paid, forwardable to whoever needs to act.</p>
            </div>
            <div className="deliv" data-testid="deliverables">
              {DELIV.map(([h, p], i) => (
                <article key={i} data-n={String(i + 1).padStart(2, "0")}><h3>{h}</h3><p>{p}</p></article>
              ))}
            </div>
            <p style={{ marginTop: 26 }}><a className="btn ghost" href="/sample-report/" data-testid="sample-report-how-link">Open the sample report</a> <a className="btn ghost" href="/research/india-app-quality-report-q4-2026" data-testid="research-report-home-link" style={{ marginLeft: 10 }}>Read the India App Quality Report</a></p>
          </div>
        </section>

        <section id="how">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Three business days</span><h2>From payment to report.</h2></div>
            <div className="tl">
              {TIMELINE.map((s, i) => (<div key={i}><b>{s[0]}</b><h4>{s[1]}</h4><p>{s[2]}</p></div>))}
            </div>
            <div className="need">
              <div><b>What we need from you</b><p>A build link — APK, Play internal testing, TestFlight — or a web URL, and your test cases. Test credentials if the app needs them. That's all.</p></div>
              <div><b>What we don't need</b><p>A call, a scope document, a device list beyond "budget Android, iPhone 11+, Chrome on Windows, Safari on Mac", or your time after the two-minute submission.</p></div>
            </div>
          </div>
        </section>

        <section className="band" id="packages">
          <div className="wrap">
            <div className="packhead">
              <div>
                <span className="eyebrow">Pricing</span>
                <h2 style={{ marginBottom: 6 }}>Fixed. Nothing further is billed.</h2>
                <p className="lede" style={{ margin: 0 }}>No hourly overage, no expenses, no change requests. A slot is a phone, a laptop or a browser — mix them in one job. Add sessions in blocks of five if you need more.</p>
              </div>
              <div className="cur" role="group" aria-label="Currency">
                {["INR", "USD"].map((c) => (
                  <button key={c} type="button" data-testid={`cur-${c}`} aria-pressed={cur === c} onClick={() => pickCur(c)}>
                    {c === "INR" ? "₹ INR" : "$ USD"}
                  </button>
                ))}
              </div>
            </div>
            <div className="packs">
              {order.map((key) => {
                const p = packages[key];
                if (!p) return null;
                const primary = key === "standard";
                return (
                  <div key={key} className={`pack ${primary ? "primary" : ""}`} data-testid={`pack-${key}`}>
                    {primary && <span className="pill blue badge">Most teams start here</span>}
                    <h3>{p.display_name}</h3>
                    <p className={`kicker ${primary ? "muted" : ""}`}>{kickers[key]}</p>
                    <div className="price">{money(p.prices[cur].display_minor, cur)}<small className="tax">{cur === "INR" ? "+ GST" : "USD"}</small></div>
                    <ul>
                      <li>Sessions <span>{p.sessions} × {p.band === "DEEP" ? "90" : "45"} min</span></li>
                      <li>Devices or browsers <span>{p.devices}, {devNote[key]}</span></li>
                      <li>Test cases <span>{cases[key]}</span></li>
                      <li>Report <span>{p.turnaround_days} business days</span></li>
                    </ul>
                    <button className="btn block" data-testid={`buy-${key}`} disabled={loading === key} onClick={() => buy(key)}>
                      {loading === key ? "Starting checkout…" : `Pay for ${p.display_name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="terms">
              <div><b>Unfilled sessions credited</b><p>If a device target can't be matched within your turnaround, the sessions that didn't run are credited back automatically and applied to your next job or re-run.</p></div>
              <div><b>Paid before work starts</b><p>Because the job is automated end to end, there is no invoice cycle. Indian customers are billed in INR with GST; everyone else in USD.</p></div>
              <div><b>Below most approval thresholds</b><p>Starter and Standard sit under the card limit of most engineering managers. That is deliberate: no procurement.</p></div>
            </div>
          </div>
        </section>

        <section id="proof-numbers">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Track record</span><h2>A decade of real-device testing.</h2></div>
            <div className="stats" data-testid="home-stats" style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
              {HOME_STATS.map(([n, l]) => (
                <div className="stat" key={l} style={{ minWidth: 180 }}><b>{n}</b><span>{l}</span></div>
              ))}
            </div>
          </div>
        </section>

        <section className="band" data-testid="home-testimonials">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">In their words</span><h2>Trusted by teams that ship.</h2></div>
            <div className="proof">
              {HOME_TESTIMONIALS.map((t, i) => (
                <div key={i}><p>&ldquo;{t.quote}&rdquo;</p><p className="muted small"><b>{t.name}</b> — {t.title}{t.note ? ` · ${t.note}` : ""}</p></div>
              ))}
            </div>
          </div>
        </section>

        <section className="dark" id="who">
          <div className="wrap">
            <div className="who">
              <div>
                <span className="eyebrow" style={{ color: "#A9B3D6" }}>Who runs it</span>
                <h2>20,000 testers. The phones and laptops your users own.</h2>
                <p className="lede">99tests has run <a href="/crowdsourced-testing">crowdsourced testing</a> for enterprise clients for over a decade. The pool is real people across Indian cities on the phones, laptops, browsers and networks that matter.</p>
                <p>Every session is timeboxed and paid whether or not bugs are found, so testers report what they see, not what earns a bonus. Testers who don't show up stop receiving offers. Testers who find real, reproducible bugs earn severity bonuses on top of the session fee.</p>
                <p>Verification is separate from testing: the person who found a bug is never the person who grades it.</p>
                <div className="logos"><span>P&amp;G</span><span>Flipkart</span><span>Infosys</span><span>Wipro</span></div>
              </div>
              <div>
                <div className="phones" aria-label="Devices commonly covered">
                  <div className="phone"><b>Redmi 12</b>MIUI 14<span className="os">Android 13</span></div>
                  <div className="phone"><b>Galaxy A15</b>One UI 6<span className="os">Android 14</span></div>
                  <div className="phone"><b>Realme C55</b>realme UI 4<span className="os">Android 13</span></div>
                  <div className="phone"><b>Vivo Y28</b>Funtouch 14<span className="os">Android 14</span></div>
                  <div className="phone ios"><b>iPhone 11</b><span className="os">iOS 17</span></div>
                  <div className="phone ios"><b>iPhone 13</b><span className="os">iOS 18</span></div>
                  <div className="laptop"><div className="scr"><b>Windows 11</b>Chrome · Edge<span className="os">1366×768 laptop</span></div><div className="base"></div></div>
                  <div className="laptop"><div className="scr"><b>macOS 15</b>Safari · Chrome<span className="os">MacBook Air</span></div><div className="base"></div></div>
                </div>
                <div className="stats">
                  <div className="stat"><b>20,000</b><span>testers registered across India</span></div>
                  <div className="stat"><b>45 / 90</b><span>minute sessions, fixed fee</span></div>
                  <div className="stat"><b>3 days</b><span>payment to report, Starter and Standard</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="scope">
          <div className="wrap">
            <div className="scope" data-testid="scope">
              <div>
                <h3>In scope</h3>
                <ul>
                  <li>Execution of your test cases on real, tester-owned phones, laptops and desktops</li>
                  <li>Exploratory testing around those cases within the session charter</li>
                  <li>Findings with steps, media and verified severity</li>
                  <li>Android and iOS builds distributed by link; web apps by URL on Chrome, Safari, Edge and Firefox</li>
                  <li>Re-runs of the same plan on a new build</li>
                </ul>
              </div>
              <div>
                <h3>Not in scope — stated plainly</h3>
                <ul>
                  <li>Not test automation, and no substitute for it</li>
                  <li>Not a security or penetration test</li>
                  <li>Not a certification or an audit under any standard</li>
                  <li>Not performance or load testing</li>
                  <li>Not desktop native apps (Windows or macOS installers) yet</li>
                  <li>Writing your test cases — bring your own, AI-generated or otherwise</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="devices">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Phones we test on</span><h2>Does your app work on the phone your users actually own?</h2></div>
            <p className="lede" style={{ marginBottom: 18 }}>Real testers run your cases on the exact budget Androids and iPhones Indians carry — each with its own skin quirks worth testing for.</p>
            <div data-testid="home-devices" style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {DEVICES.map((d) => (
                <a key={d.slug} className="btn ghost" href={`/test-app-on/${d.slug}`} data-testid={`home-device-${d.slug}`}>{d.model}</a>
              ))}
            </div>
          </div>
        </section>

        <section className="band" id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="faq">
              {[...BUYER_FAQ_HOME, ...FAQ].map(([q, a], i) => (
                <details className="f" key={i}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Ship this release knowing it works on a Redmi — and on Safari.</h2>
                <p>Pay, upload, and testers are on it within the hour. Report in three business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      {pending && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,30,70,.5)", zIndex: 60, display: "grid", placeItems: "center", padding: 20 }} onClick={() => !loading && setPending(null)} data-testid="checkout-modal">
          <div className="panel" style={{ background: "var(--paper)", borderRadius: "var(--r-l)", padding: 28, width: "100%", maxWidth: 460, boxShadow: "var(--shadow)" }} onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow">Almost there</span>
            <h3 style={{ marginBottom: 6 }}>Pay for {packages[pending]?.display_name}</h3>
            <p className="muted small" style={{ margin: "0 0 14px" }}>We send the invoice, status link and report to this email. Any credit from unfilled sessions on this email is applied automatically.</p>
            <div className="field"><label htmlFor="co-email">Your email</label>
              <input id="co-email" type="email" autoFocus data-testid="checkout-email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmBuy()} /></div>
            {credit > 0 && (
              <div className="small" style={{ background: "rgba(14,124,75,.08)", border: "1px solid rgba(14,124,75,.3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }} data-testid="checkout-credit">
                Credit available: <b>{money(credit, cur)}</b>. You'll pay <b>{money(Math.max(0, (packages[pending]?.prices?.[cur]?.display_minor || 0) - credit), cur)}</b>{(packages[pending]?.prices?.[cur]?.display_minor || 0) - credit <= 0 ? " — fully covered, no card needed." : "."}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost" disabled={!!loading} onClick={() => setPending(null)} data-testid="checkout-cancel">Cancel</button>
              <button type="button" className="btn blue" disabled={!!loading} onClick={confirmBuy} data-testid="checkout-continue">{loading ? "Starting checkout…" : credit > 0 && (packages[pending]?.prices?.[cur]?.display_minor || 0) - credit <= 0 ? "Use credit and start" : "Continue to payment"}</button>
            </div>
          </div>
        </div>
      )}
      <footer className="site-footer"><div className="wrap">
        <div>© 2026 99tests · Bengaluru<br />
          <span className="small" data-testid="footer-identity">Founded 2010 · Super Founder Hub. UKIBC, No 9, Prestige Blue Chip Tech Park, Hosur Main Road, Dairy Colony, Adugodi, Bengaluru, Karnataka 560029, India.</span><br />
          <span className="small">Not test automation. Not a certification or an audit under any standard.</span>
          <br />
          <a href="https://index.dodopayments.com/99tests?utm_source=index&utm_medium=badge&utm_campaign=embed&utm_content=tool-99tests" target="_blank" rel="noopener noreferrer" data-testid="dodo-index-badge" style={{ display: "inline-block", marginTop: 14, verticalAlign: "bottom" }}>
            <img src="https://index.dodopayments.com/99tests/badge.svg?theme=dark&width=250&height=50" alt="99tests badge" width="250" height="50" loading="lazy" decoding="async" style={{ display: "inline-block", border: 0, width: "100%", maxWidth: 250, height: "auto", maxHeight: 50 }} />
          </a>
          <a href="https://devhunt.org/tool/99tests" target="_blank" rel="noopener noreferrer" title="99tests on DevHunt" data-testid="devhunt-badge" style={{ display: "inline-block", marginTop: 14, marginLeft: 10, verticalAlign: "bottom" }}>
            <img src="https://devhunt.org/badge/99tests.svg?theme=light" alt="99tests - Featured on DevHunt" width="220" height="54" loading="lazy" decoding="async" style={{ display: "inline-block", border: 0, width: "100%", maxWidth: 220, height: "auto", maxHeight: 54 }} />
          </a>
          <a href="https://www.betterlaunch.co/product/99tests" target="_blank" rel="noopener noreferrer" title="View 99tests on Better Launch" data-testid="betterlaunch-badge" style={{ display: "inline-block", marginTop: 14, marginLeft: 10, verticalAlign: "bottom" }}>
            <img src="https://www.betterlaunch.co/badge-find-light.svg" alt="View 99tests on Better Launch" width="200" height="56" loading="lazy" decoding="async" style={{ display: "inline-block", border: 0, width: "100%", maxWidth: 200, height: "auto", maxHeight: 56 }} />
          </a>
          <a href="https://launchstag.com/p/99tests" target="_blank" rel="noopener noreferrer" title="Featured on Launchstag" data-testid="launchstag-badge" style={{ display: "inline-block", marginTop: 14, marginLeft: 10, verticalAlign: "bottom" }}>
            <img src="https://launchstag.com/badge-light.svg" alt="Featured on Launchstag" width="198" height="62" loading="lazy" decoding="async" style={{ display: "inline-block", border: 0, width: "100%", maxWidth: 198, height: "auto", maxHeight: 62 }} />
          </a></div>
        <div data-testid="footer-explore">
          <a href="/crowdsourced-testing">Crowdsourced testing</a>
          <a href="/real-device-testing-india">Real-device testing in India</a>
          <a href="/mobile-app-testing-india">Mobile app testing in India</a>
          <a href="/cross-browser-testing">Cross-browser testing</a>
          <a href="/exploratory-testing">Exploratory testing</a>
          <a href="/test-ai-generated-test-cases">Test AI-generated test cases</a>
          <a href="/vs-device-clouds-and-automation">vs Device clouds &amp; automation</a>
        </div>
        <div data-testid="footer-compare">
          <span className="small" style={{ marginRight: 6 }}>Compare 99tests</span>
          <a href="/vs/utest">vs uTest</a>
          <a href="/vs/testlio">vs Testlio</a>
          <a href="/vs/browserstack-app-live">vs BrowserStack App Live</a>
          <a href="/vs/test-io">vs test IO</a>
          <a href="/vs/global-app-testing">vs Global App Testing</a>
        </div>
        <div data-testid="footer-tools">
          <span className="small" style={{ marginRight: 6 }}>Free tools</span>
          <a href="/tools/website-checker">Website checker</a>
          <a href="/tools/play-store-bug-scanner">Play Store bug scanner</a>
          <a href="/test-cases">Test case library</a>
          <a href="/tools/test-plan-builder">Test plan builder</a>
          <a href="/tools/qa-cost-calculator">QA cost calculator</a>
          <a href="/app-testing-cost">App testing cost by app type</a>
          <a href="/research/app-quality">App quality by category</a>
        </div>
        <div>
          <a href="/about" data-testid="footer-about">About</a>
          <a href="/join">Become a tester</a>
          <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
          <a href="/research" data-testid="footer-research">Research</a>
          <a href="https://blog.99tests.com/" data-testid="footer-blog">Blog</a>
          <a href="https://outrank.so/" target="_blank" rel="sponsored nofollow noopener" data-testid="footer-outrank">Outrank</a>
          <a href="/jobs" data-testid="footer-my-jobs">My jobs</a>
          <a href="/terms">Client terms</a>
          <a href="/tester-terms">Tester terms</a>
          <a href="/privacy">Privacy</a>
          <a href="mailto:hello@99tests.com">hello@99tests.com</a>
        </div>
      </div></footer>
    </>
  );
}
